// File: frontend/src/utils/entryImageUrl.ts

import { API_BASE_URL } from "../constants";

/**
 * Inline entry images are referenced inside entry content as (gorsel: <ref>).
 *
 * Two reference formats exist and both must keep working:
 *
 *  1. Opaque image id (current format) — "8f14e45fceea167a5a36dedd4bea2543.jpg".
 *     32 random hex characters plus the image extension. It carries no
 *     directory path, no user id and no upload timestamp, so an entry's text
 *     reveals nothing about how the server stores the file.
 *
 *  2. Absolute URL (legacy format) — entries written before the opaque id was
 *     introduced embed the full media URL. They are rendered as-is so old
 *     entries keep displaying their images without any data migration.
 *
 * The opaque id is served by the backend at GET /api/media/i/{id}, which is
 * also opaque: it exposes neither the storage directory nor the uploader.
 */

/** Opaque image id: 32 lowercase hex characters + a browser-safe extension. */
export const ENTRY_IMAGE_ID_PATTERN = /^[a-f0-9]{32}\.(?:jpg|jpeg|png|webp)$/;

/** True when the reference is an absolute URL, i.e. the legacy format. */
const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

/**
 * Turns a raw (gorsel: ...) reference into the src the browser should load.
 *
 * @param ref Raw reference as written in the entry content
 * @returns Absolute or API-relative image URL, or "" when the reference is
 *          neither a valid opaque id nor an absolute URL. Callers render
 *          nothing for an empty result instead of a broken image.
 */
export const resolveEntryImageSrc = (ref: string | null | undefined): string => {
  const value = (ref ?? "").trim();
  if (!value) return "";

  // Legacy entries: the full URL is already embedded in the content
  if (ABSOLUTE_URL_PATTERN.test(value)) return value;

  // Anything that is not a well-formed opaque id is refused rather than being
  // pasted into a src attribute — this keeps arbitrary user text out of the URL
  if (!ENTRY_IMAGE_ID_PATTERN.test(value)) return "";

  return `${API_BASE_URL}/api/media/i/${value}`;
};
